<template>

  <div>

    <!--
      XOL-526: the drawer is open to every category again. XOL-443 suppressed
      it for Backhaul because RequestDetails.initRequest() unconditionally
      fetched the COUNTERPARTY telco's contacts (the org-scoped contacts read, for
      the owner organisation_id).

      On a Backhaul-raised RTB that organisation_id is the customer (the
      telco), not the caller. That route carries tenant.towerco:orgId, whose
      cross-tenant read is Tower-Company-only (XOL-345/351), so a Backhaul
      caller got a 403 mismatch and the drawer opened with an "Unable to fetch
      Contacts - Not authorized for this tenant" toast.

      XOL-526 (customer support ticket: a backhaul provider must be able to
      work an RTB it's named on, same as the telco) fixes the actual gap
      instead: RequestDetails.vue guarded that fetch with
      canReadOrgScoped(actionData.organisation_id), so a provider viewer
      never made the call that used to 403. (XOL-586: that fetch, and the
      seeding it fed, are gone: the e-mail lists are live and the API reads
      the contacts.) Re-hiding the whole drawer here is no longer the smaller
      disclosure — it hid the provider's own RTB from itself. See docs/plans/2026-09-23-xol-526-backhaul-rtb-access.md.

      xolas-tms-e2e cites this file:line for the XOL-443 gate — flag the move
      on the ticket, per that plan's §4 S7.
    -->
    <xolas-table
        :title="'Request to ' + this.heading"
        :id="this.type.toLowerCase() + '-action-table'"
        :table-ref="this.type.toLowerCase() + '-action-table'"

        :url="'/view/actions/' + (this.telcoId ? this.type + '/' + this.telcoId : this.type + '/null') + '/' + reqStatus"

        :fields="fields"

        :sort-field="sortField + '|' + sortDir"
        :sort-direction="sortDir.toUpperCase()"

        :search-placeholder="searchPlaceholder"
        :show-refresh="true"
        :show-search="true"

        :layer-component="disableLayer ? null : 'request'"
        :layer-args="{ id: 'id' }"
        @loaded="onLoaded"
        ref="reqTable">

        <template #actions>
          <request-action
            v-if="canCreateRequest()"
            :type="type"
            :row-data="actionStruct" />
        </template>

        <!-- Mirrors dev/production order: filter strip + sort dropdown sit
             BETWEEN the search/filter-bar and the data rows, not above them.
             -->
        <template #below-filter>
          <request-stats
            class="request-stats"
            :status="reqStatus"
            :active="active"
            :rejected="rejected"
            :closed="closed"
            :mine="type === 'RTA' ? mineCount : null"
            :change="changed" />

          <el-row class="request-sort-bar" type="flex" justify="center" align="middle">
            <el-col align="left" :span="3">
              <i class="text-muted">Sort by:</i>
            </el-col>
            <el-col align="center" :span="18">
              <el-select v-model="sortField" size="small" class="request-sort-bar__select" @change="applySort" placeholder="Sort field">
                <el-option v-for="opt in sortOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
              </el-select>
            </el-col>
            <el-col align="right" :span="3">
              <el-tooltip :content="sortField + ': ' + sortDir" placement="top">
                <el-switch
                  v-model="sortDir"
                  active-icon-class="fa fa-sort-amount-up text-success"
                  inactive-icon-class="fa fa-sort-amount-down text-danger"
                  active-value="asc"
                  inactive-value="desc"
                  @change="applySort" />
              </el-tooltip>
            </el-col>
          </el-row>
        </template>

        <!-- XolasTable forwards #empty into el-table's empty slot.
             Element UI 2.x does NOT ship <el-empty> (that's Element Plus / Vue 3),
             so we hand-roll the empty state. -->
        <template #empty>
          <!-- XOL-67 (B7), UX revised section 2: the empty Needs My Action bucket is one line. -->
          <p v-if="reqStatus === 'mine'" class="text-muted requests-empty__mine">No acquire requests need your action.</p>
          <div v-else class="requests-empty">
            <i class="fa fa-share-alt requests-empty__icon" />
            <p class="requests-empty__text">No {{ heading.toLowerCase() }} requests</p>
            <p class="requests-empty__sub">There are no {{ reqStatus }} requests in this category yet.</p>
          </div>
        </template>

        <template #request-details="props">

          <el-row :gutter="8.52" :class="formatBG(props.rowData.actionType)" style="padding: 0.852em">

            <el-col :xs="24" :sm="24" :md="24" :lg="12">

            <el-col :span="24">

              <h3 :class="formatRequest(props.rowData.actionType)">
                <org-badge v-if="!isTelco()" :short-name="props.rowData.shortName" :color="props.rowData.colourCode" />
                <div class="pull-right" v-if="props.rowData.actionType === 'RTB' && props.rowData.provider_id">
                  <h5>Provider: <org-badge :short-name="props.rowData.providerShortName" :color="props.rowData.providerColourCode"  />&nbsp;<b class="text-black">{{props.rowData.backhaulType}}</b></h5>
                </div>
              </h3>


              <h4 :class="formatRequest(props.rowData.actionType)">
                <span v-if="(props.rowData.status === 'Rejected' || props.rowData.status === 'Closed')">
                  <b :class="(props.rowData.status === 'Rejected' ? 'text-warning' : 'text-success')">{{props.rowData.status}}</b>
                  &nbsp;|&nbsp;</span><b>
                    <el-badge v-if="props.rowData.status === 'NEW' || props.rowData.status === 'Pending'"
                      :value="props.rowData.status"
                      :type="props.rowData.status === 'Pending' ? 'warning' : 'danger'">
                      {{props.rowData.siteName}}
                    </el-badge>
                    <span v-else>{{props.rowData.siteName}}</span>
                  </b>
                <span v-if="props.rowData.telcoSiteId">&nbsp;({{props.rowData.telcoSiteId}})</span>
              </h4>


              <!-- XOL-67 (B7): an RTA row has its own stage tag, cue line and option summary, and the fixed five-step stepper
                   from rtaStage.stepState (no el-progress). Every other request type keeps the markup below it. -->
              <template v-if="isRta(props.rowData)">
                <div class="rta-cue">
                  <el-tag size="small" effect="light" :type="rtaStageMeta(props.rowData).type" class="rta-cue__stage"><i :class="'fa ' + rtaStageMeta(props.rowData).icon" aria-hidden="true"></i>{{ rtaStageMeta(props.rowData).label }}</el-tag>
                  <el-tag v-if="rtaSiteGone(props.rowData)" class="rta-chip" size="mini" effect="light" type="info"><i class="fa fa-link-slash" aria-hidden="true"></i>Site deleted</el-tag>
                  <el-tag v-if="rtaChanged(props.rowData)" class="rta-chip" size="mini" effect="light" type="warning"><i class="fa fa-pencil-alt" aria-hidden="true"></i>Changed since submitted</el-tag>
                  <span class="rta-cue__line">
                    <template v-if="rtaCue(props.rowData).kind === 'turn'">
                      <el-tag size="mini" effect="light" type="warning"><i class="fa fa-arrow-right" aria-hidden="true"></i>Your turn</el-tag>
                      <span>{{ rtaCue(props.rowData).text }}</span>
                    </template>
                    <span v-else-if="rtaCue(props.rowData).kind === 'terminal'">{{ rtaCue(props.rowData).text }}</span>
                    <span v-else>Waiting on: <b>{{ rtaCue(props.rowData).name }}</b>{{ rtaCue(props.rowData).ending }}</span>
                  </span>
                </div>

                <small class="rta-summary">{{ rtaSummary(props.rowData) }}</small>

                <div class="hidden-sm-and-down">
                  <span class="rta-visually-hidden">{{ rtaStageText(props.rowData) }}</span>
                  <el-steps class="rta-steps" :active="rtaStepper(props.rowData).active" finish-status="success" align-center direction="horizontal">
                    <el-step
                      v-for="(st, i) in rtaStepper(props.rowData).steps"
                      :key="st.no"
                      :status="rtaStepper(props.rowData).statuses[i]"
                      :description="rtaStepper(props.rowData).descriptions[i]">
                      <template #icon>
                        <i :class="'fa ' + st.icon" style="background: inherit;"></i>
                      </template>
                    </el-step>
                  </el-steps>
                </div>
              </template>

              <el-steps v-else class="hidden-sm-and-down" :active="props.rowData.currentStep - 1" :finish-status="formatStep(props.rowData.actionType)" align-center direction="horizontal">
                <el-step v-for="s in props.rowData.stepWorkflow" :key="s.stepName" :description="s.stepName">
                  <template #icon>
                    <i :class="s.stepIcon" style="background: inherit;"></i>
                  </template>
                </el-step>
              </el-steps>

            </el-col>


            <el-col style="padding-top: 7.41px;" :xs="24" :sm="24" :md="24" :lg="24" align="left">

              <el-row :gutter="5.28">
                <el-col v-if="!isRta(props.rowData)" :xs="24" :sm="24" :md="24" :lg="14" align="left">
                 <el-progress v-if="props.rowData.currentStep >= 0 && props.rowData.totalSteps"
                    :stroke-width="26"
                    :percentage="(props.rowData.currentStep / props.rowData.totalSteps * 100)"
                    :color="progessBar(props.rowData.actionType)"
                    :text-inside="true" />
               </el-col>

               <el-col :xs="24" :sm="24" :md="24" :lg="isRta(props.rowData) ? 24 : 10" :align="props.rowData.totalSteps && !isRta(props.rowData) ? 'center' : 'left'">

                <!-- eslint-disable-next-line vue/no-v-html -- formatter output, enum → coloured <b> markup -->
                <span v-html="formatPeriod(props.rowData.Period)" />&nbsp;|&nbsp;
                {{(props.rowData.Period === 'Finished' ? '' : 'Due:')}}
                {{formatDate((props.rowData.Period === 'Finished' ? props.rowData.actualDate : props.rowData.targetDate))}}
                &nbsp;|&nbsp;
                <b>{{formatFriendlyDate((props.rowData.Period === 'Finished' ? props.rowData.actualDate : props.rowData.targetDate))}}</b>

               </el-col>


               <el-col v-if="isDesktop && isTowerCo()" :span="24">
               <br />
                <h5>Client: <b>{{props.rowData.name}}</b></h5>
               </el-col>

            </el-row>


              <small v-if="isRta(props.rowData)" class="rta-ref">Ref: <i>{{ rtaRefLine(props.rowData) }}</i></small>
              <small v-else>Ref: <i>{{props.rowData.siteId}}-{{props.rowData.id}}</i></small>

            </el-col>

          </el-col>

            <el-col :xs="24" :sm="24" :md="24" :lg="12">
              <site-card :site-data="mapRow(props.rowData)" use-leaflet hide-site height="230px" />
            </el-col>
          </el-row>


        </template>

        <template #rts-controls="props">
          <rtx-action-delete-control :row-data="props.rowData" />
        </template>

    </xolas-table>

  </div>

</template>

<script>

import DateMixins from '@/mixins/DateMixins'
import SiteCard from '@/components/sites/SiteCard'
import axios from '@/lib/axiosClient'
import { sanitiseHtml } from '@/lib/sanitiseHtml'
import * as rtaStage from '@/lib/rtaStage'
import * as rtaPanel from '@/lib/rtaPanel'

import RequestAction from '@/components/actions/RequestAction'
import RequestStats from '@/components/requests/RequestStats'
import RtxActionDeleteControl from '@/components/requests/RtxActionDeleteControl'

export default {

  mixins: [DateMixins],

  components: {
    SiteCard,
    RequestAction,
    RequestStats,
    RtxActionDeleteControl
  },

  props: {
    sharer: {
      type: Number,
      default: null
    },

    heading: {
      type: String,
      default: 'Share'
    },

    type: {
      type: String,
      default: 'RTS'
    },

    status: {
      type: String,
      default: 'active'
    },

    rejected: {
      type: Number,
      default: null
    },

    active: {
      type: Number,
      default: null
    },

    closed: {
      type: Number,
      default: null
    },

    disableLayer: {
      type: Boolean,
      default: false
    }

  },


  methods: {

    /**
     * XOL-393: whether the create button (Share / Upgrade / Acquire / …) is rendered.
     *
     * The exact negation of the ROLE clause in RequestAction's `:disableButton`:
     *   disable when  isTowerCo() && !isManager('project|finance|admin')
     *   so show when  !isTowerCo() || isManager('project|finance|admin')
     *
     * Note the `isTowerCo()` qualifier is load-bearing. The role gate applies ONLY to
     * Tower Co; for every other category (Telco, Backhaul, Contractor, Landlord,
     * Authority, Utility, Supplier) RequestAction renders the button ENABLED regardless
     * of the manager flag, so this must not hide it from them.
     *
     * View-only is NOT checked here. RequestAction self-gates on `!isViewOnly()`
     * (XOL-393), which keeps that rule identical on all three surfaces the button
     * renders on. Duplicating it here is what caused the drift this ticket fixes.
     *
     * See docs/UX-architecture/access/edit-access-register.md for the register row.
     */
    canCreateRequest () {
      return !!this.actionStruct &&
        (!this.isTowerCo() || this.isManager('project|finance|admin'))
    },

    /* ---- XOL-67 (B7): RTA rows. Every derivation is rtaStage / rtaPanel (pure); these only hand them the row and who is looking.
       The row is a vw_actions row: the flat option-summary keys (DEV-8), `state` (state.rta.siteDeleted) and `name`, the client. ---- */

    isRta (row) {
      return !!row && row.actionType === 'RTA'
    },

    // XOL-67 (v2.1): an RTA has no site until Finalize, so the row's latitude/longitude (the site's) are empty and the card
    // drew no map. Until then the map shows the requested point, state.nominal. Every other row is passed through as is.
    mapRow (row) {
      if (!this.isRta(row) || (row.latitude && row.longitude)) return row
      var nominal = row.state && row.state.nominal
      if (!nominal || !nominal.latitude || !nominal.longitude) return row
      return Object.assign({}, row, { latitude: nominal.latitude, longitude: nominal.longitude })
    },

    // The list has no `completed_at`; a finished RTA's date is its `approved_at` (set by Complete).
    rtaRow (row) {
      return row.completed_at ? row : Object.assign({}, row, { completed_at: row.approved_at || null })
    },

    // The row's bottom line (UX 2.9, decisions 138, 163): the RTA's reference, never the v1 `{siteId}-{id}`. A Completed RTA adds the site it
    // created ("RTA-000918 · GBK-0042"); if that site was deleted since, the Site ID it had is kept from state.rta.siteDeleted. An open RTA
    // has no site of its own, so a legacy row's `siteId` is not shown before Complete.
    rtaRefLine (row) {
      var ref = rtaStage.referenceOf(row)

      if (row.status !== 'Closed') return ref

      var gone = rtaStage.siteDeleted(row)
      var siteId = row.siteId || (gone && gone.siteId) || ''

      return siteId ? ref + ' · ' + siteId : ref
    },

    rtaStageMeta (row) {
      return rtaStage.stageMeta(row.status)
    },

    rtaSiteGone (row) {
      return !!rtaStage.siteDeleted(row)
    },

    // The Tower Co's "Changed since submitted" chip. The client is told to submit instead (its cue line says so).
    rtaChanged (row) {
      return rtaStage.showsChangedChip(row, this.isTowerCo())
    },

    rtaCue (row) {
      return rtaPanel.cue(this.rtaRow(row), {
        role: this.isTowerCo() ? 'tcc' : 'client',
        telco: row.name || row.shortName || '',
        towerCo: this.towerCoName(),
        org: this.org(),
        fmtDate: this.formatDate
      })
    },

    rtaSummary (row) {
      return rtaStage.optionSummary(row, { telco: row.name || row.shortName || '', towerCo: this.towerCoName() }).text
    },

    // The five steps, per-step status from stepState; a Completed RTA is all five finished, and a Rejected one never reaches step 5.
    rtaStepper (row) {
      var state = rtaStage.stepState(row)

      return {
        steps: rtaStage.STEPS,
        statuses: state.statuses,
        active: state.terminal === 'completed' ? 5 : state.current - 1,
        descriptions: rtaStage.STEPS.map(function (st, i) {
          return i === 4 && state.terminal === 'rejected' ? 'Not reached' : st.name
        })
      }
    },

    // What a screen reader hears for the stepper.
    rtaStageText (row) {
      var label = rtaStage.stageMeta(row.status).label
      var state = rtaStage.stepState(row)

      return state.terminal ? label : 'Stage ' + state.current + ' of 5: ' + label
    },

    // The Needs My Action count (decision 38): the `total` of the RTA `mine` bucket at one row per page. It is read after every
    // list load, so it follows a refresh. A failed read keeps the last good count (null, hidden, until the first one lands); an
    // answer that is not the newest is dropped.
    fetchMine () {
      var seq = ++this.mineSeq
      var url = this.getEndpoint('/view/actions/RTA/' + (this.telcoId ? this.telcoId : 'null') + '/mine')

      return axios.get(url, { params: { page: 1, per_page: 1 } }).then(function (res) {
        if (seq !== this.mineSeq) return

        var total = res && res.data ? Number(res.data.total) : NaN

        if (!isNaN(total)) {
          this.mineCount = total
        }
      }.bind(this)).catch(function () {
        // keep the last good count
      })
    },

    onLoaded () {
      if (this.type === 'RTA') {
        this.fetchMine()
      }
    },

    formatStatus (value) {
      var color = ''

      switch (value) {
        case 'Closed':
          color = '#67C23A'
          break

        case 'Rejected':
          color = '#E6A23C'
          break

        default:
          color = '#606266' // '#303133'

      }

      return '<span style="color: ' + color + '">' + value + '</span>'
    },

    formatStep (value) {
      let cls = ''

      switch (value) {
        case 'RTS':
          cls = 'success'
          break

        case 'RTU':
          cls = 'primary'
          break

        case 'RTB':
          cls = 'info'
          break

          case 'RTA':
            cls = 'warning'
            break

         case 'RTD':
           cls = 'danger'
           break

      }

      return cls
    },

    formatRequest (value) {
      let cls = ''

      switch (value) {
        case 'RTS':
          cls += 'text-success'
          break

        case 'RTU':
          cls += 'text-primary'
          break

        case 'RTB':
          cls += 'text-info'
          break

          case 'RTA':
            cls += 'text-warning'
            break

            case 'RTD':
              cls += 'text-danger'
              break


      }

      return cls
    },

    formatBG (value) {
      let cls = ''

      switch (value) {
        case 'RTA':
          cls += 'bg-warning-lighten-2'
          break

        case 'RTS':
          cls += 'bg-success-lighten-2'
          break

        case 'RTU':
          cls += 'bg-primary-lighten-1'
          break

        case 'RTB':
          cls += 'bg-info-lighten-2'
          break

        case 'RTD':
           cls += 'bg-danger-lighten-2'
           break

      }

      return cls
    },

    // Method name retains legacy "progessBar" spelling (missing 'r') —
    // the slot template references this exact identifier, and renaming
    // would diverge from `main`'s template payload without behavior change.
    // Intentional, do not rename.
    progessBar (value) {
      let cls = '#C6E2FF'

      switch (value) {
        case 'RTA':
          cls = '#E6A23C'
          break

        case 'RTS':
          cls = '#67C23A'
          break

        case 'RTU':
          cls = '#409EFF'
          break

        case 'RTB':
          cls = '#909399'
          break

      case 'RTD':
         cls = 'indianred'
         break

      }

      return cls
    },

    formatDays (value) {
      var color = '#606266'

      if (value < 0) {
        color = 'indianred'
      }

      return '<span style="color: ' + color + '">' + value + '</span>'
    },

    formatPeriod (value) {
      var color = '#606266'

      if (value === 'Finished') {
        color = '#67C23A'
      }

      if (value === 'Overdue') {
        color = 'indianred'
      }

      if (value === 'Month') {
        color = 'slateblue'
      }

      if (value === 'Week') {
        color = 'goldenrod'
      }

      return sanitiseHtml('<b style="color: ' + color + '">' + value + '</b>')
    },

    changed (req) {
      this.reqStatus = req
      // reqStatus mutates → :url recomputes → xolas-table gets new prop →
      // explicit reload ensures refetch even if url string stays identical.
      // XOL-67: reload() starts at page 1 — another bucket's page number is meaningless here.
      this.$nextTick(function () {
        if (this.$refs.reqTable) {
          this.$refs.reqTable.reload()
        }
      }.bind(this))
    },

    applySort () {
      this.$nextTick(function () {
        if (this.$refs.reqTable) {
          this.$refs.reqTable.reload()
        }
      }.bind(this))
    }

  },


  computed: {
    // The Acquire tab's search also finds an RTA by its reference (the API matches `rtaReference`, API-12); the other tabs keep their words.
    searchPlaceholder () {
      return this.type === 'RTA'
        ? 'Names, RTA-…, Site IDs, Status, Timeline...'
        : 'Site Names, Site IDs, Status, Timeline...'
    },

    // Null-safe wrapper for getTableStruct('actions'). Meta is loaded from
    // an async bootstrap XHR; calling getTableStruct() in the template before
    // hydration throws (getMeta returns null, .struct on null crashes render).
    // Returns null until meta is ready; the v-if on <request-action> defers
    // mount until the struct exists.
    actionStruct () {
      try {
        const meta = this.getMeta && this.getMeta('actions')
        return meta && meta.struct ? this.clone(meta.struct) : null
      } catch (e) {
        return null
      }
    }
  },


  watch: {

    sharer: function (value) {
      this.telcoId = value
      this.$nextTick(function () {
        if (this.$refs.reqTable) {
          this.$refs.reqTable.reload()
        }
      }.bind(this))
    }

  },


  data () {
    return {
      reqStatus: this.status,

      telcoId: this.sharer,

      // XOL-67 (B7): the RTA tab's Needs My Action count; null until the first one has loaded (the tile is hidden), then the last good one.
      mineCount: null,
      mineSeq: 0,

      // sort dropdown + asc/desc switch — mirrors legacy DataTableFilterBar.
      // Pipe-formatted into `:sort-field` (e.g. "targetDate|desc") so the API's
      // explode('|', sort) gets [field, direction].
      sortField: 'targetDate',
      sortDir: 'desc',
      sortOptions: [
        { value: 'targetDate', label: 'Target Date' },
        { value: 'Period', label: 'Period' },
        { value: 'status', label: 'Status' },
        { value: 'siteName', label: 'Site Name' },
        { value: 'currentStep', label: 'Current Step' },
        { value: 'shortName', label: 'Organisation' }
      ],

      fields: [
        {
          name: '__slot:request-details',
          title: '',
          minWidth: '600'
        },
        {
          name: '__slot:rts-controls',
          title: '',
          width: '60',
          visible: this.isTowerCo()
        }
      ]
    }
  },


  mounted () {
    if (!this.telcoId && this.isTelco()) {
      this.telcoId = Number(this.org().id)
    }
  }
}
</script>

<style scoped>
/* Empty state — Element UI 2.x has no <el-empty>; hand-rolled. */
.requests-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px;
  color: #909399;
}
.requests-empty__icon {
  font-size: 48px;
  color: #DCDFE6;
}
.requests-empty__text {
  margin: 12px 0 0;
  font-size: 14px;
}
.requests-empty__sub {
  margin-top: 4px;
  font-size: 12px;
  color: #C0C4CC;
}

/* XOL-67 (B7): the RTA row's stage tag, cue line, option summary and stepper (the mock's rta-cue rules, UX v3 section 2). */
.rta-cue {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin: 4px 0 6px;
  font-size: 13px;
  color: #606266;
}
.rta-cue .el-tag .fa,
.rta-chip .fa { margin-right: 4px; }
.rta-cue__line { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.rta-cue__line b { color: #303133; }
.rta-summary { display: block; margin: 0 0 8px; color: #606266; }
.rta-visually-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
/* Element sets `word-break: break-all` on `.el-table .cell`, and a step's description inherits it: "Tower Co Decision" broke
   mid-word ("Tower Co Dec / ision"). The stepper of an RTA row (and only it) breaks between words again. */
.rta-steps { word-break: normal; overflow-wrap: normal; }
.requests-empty__mine { margin: 0; padding: 16px; text-align: center; }

/* RequestStats filter strip — sits in #below-filter slot, between search
   and rows. No card chrome (matches main): el-row provides the tinted
   bg-bordered background and the row's natural height. */
.request-stats {
  margin: 8px 0 0;
}
/* Sort row — mirrors legacy DataTableFilterBar's sort strip:
   #F2F6FC tinted bg, tight padding, negative margin-top so the strip
   visually merges with the RequestStats above. */
.request-sort-bar {
  background-color: #F2F6FC;
  width: 100%;
  padding: 0.528em;
  margin-top: -16px;
  margin-bottom: 12px;
}
.request-sort-bar__select {
  width: 100%;
}
</style>
